import React, { useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Enhanced FormInput Component with Error Handling
const FormInput = ({ label, type, name, value, onChange, error, required = false, rows = null }) => {
  if (type === 'textarea') {
    return (
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-medium mb-2">
          {label}
        </label>
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          rows={rows || 4}
          required={required}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={`Enter your ${label.toLowerCase()}`}
        />
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="mb-6">
      <label className="block text-gray-700 text-sm font-medium mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
        placeholder={`Enter your ${label.toLowerCase()}`}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

// ContactSection Component
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear API error when user starts typing
    if (apiError) {
      setApiError('');
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateMobile = (mobile) => {
    // Basic mobile validation - allows digits, spaces, dashes, and plus sign
    const mobileRegex = /^[+]?[0-9\s-()]{10,}$/;
    return mobileRegex.test(mobile);
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required.';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters long.';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    // Mobile validation
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required.';
    } else if (!validateMobile(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid mobile number.';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required.';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long.';
    } else if (formData.message.trim().length > 1000) {
      newErrors.message = 'Message must not exceed 1000 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setApiError('');
    
    try {
      // Make API call to backend using axios
      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/contact`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // console.log('Contact form submitted successfully');
      
      // Show success message
      setSuccess(true);
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          mobile: '',
          message: ''
        });
        setSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Contact form error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        setApiError('Failed to send message. Please try again later.');
      } else if (error.request) {
        // Network error
        setApiError('Network error. Please check your connection and try again.');
      } else {
        // Other error
        setApiError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section className="py-16 lg:py-12">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Message Sent Successfully!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for contacting us. We'll get back to you within 24 hours.
            </p>
            <button
              onClick={() => setSuccess(false)}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
            >
              Send Another Message
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-12">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {/* Main Contact Container */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            
            {/* Left Column - Contact Information */}
            <div className="p-8 lg:p-12 text-white" style={{ backgroundColor: '#0a2342' }}>
              {/* Phone Icon */}
              <div className="flex justify-center mb-6">
                <img 
                  src="/assets/contact/phone_contact.png" 
                  alt="Contact Phone Icon" 
                  className="w-16 h-16 object-contain"
                />
              </div>
              
              {/* Introduction Text */}
              <p className="text-center mb-8 text-gray-300 leading-relaxed">
                For any enquiries please reach out to us. Our support and assistance team will get back to you within 24 hours.
              </p>
              
              {/* Divider */}
              <div className="border-t border-green-500 mb-8"></div>
              
              {/* Address Section */}
              <div className="mb-8">
                <h3 className="font-bold text-lg mb-3" style={{ fontSize: 'calc(1.125rem * 1.8)' }}>Address</h3>
                <p className="text-gray-300 leading-relaxed" style={{ fontSize: 'calc(1rem * 1.5)' }}>
                  Sakaram Patil Marg, Sector 1A,<br />
                  CBD Belapur, Navi Mumbai,<br />
                  Maharashtra 400614
                </p>
              </div>
              
              {/* Contact Number Section */}
              <div>
                <h3 className="font-bold text-lg mb-3" style={{ fontSize: 'calc(1.125rem * 1.8)' }}>Contact No.</h3>
                <p className="text-gray-300" style={{ fontSize: 'calc(1rem * 1.5)' }}>+91 7900146175</p>
              </div>
            </div>
            
            {/* Right Column - Contact Form */}
            <div className="bg-white p-8 lg:p-12">
                             {/* Form Title */}
               <h2 className="text-2xl font-bold text-gray-900 mb-8">Fill the form below</h2>
               
               {/* API Error Display */}
               {apiError && (
                 <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                   <p className="text-red-600 text-sm">{apiError}</p>
                 </div>
               )}
               
               {/* Contact Form */}
               <form onSubmit={handleSubmit}>
                <FormInput
                  label="Your name:"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={errors.name}
                  required
                />
                
                <FormInput
                  label="Email ID:"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  required
                />
                
                <FormInput
                  label="Mobile No:"
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  error={errors.mobile}
                  required
                />
                
                <FormInput
                  label="Message:"
                  type="textarea"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  error={errors.message}
                  required
                  rows={4}
                />
                
                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200 ${
                    loading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-green-500 hover:bg-green-600'
                  } text-white`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending Message...
                    </div>
                  ) : (
                    'Submit'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ContactUsPage Component
const ContactUsPage = () => {
  const [searchParams] = useSearchParams();
  /** TrendVector mobile WebView (`tv_embed=1`): hide site chrome; back button is in the app. */
  const isMobileEmbed = searchParams.get('tv_embed') === '1';

  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <div 
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: isMobile ? 'none' : 'url("/assets/contact/contacy_bg.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {!isMobileEmbed && <Navbar />}
      
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <ContactSection />
      </div>

      {!isMobileEmbed && <Footer />}
    </div>
  );
};

export default ContactUsPage;
